/* ============================================================
   HSP [table] — progressive enhancement.

   Styling lives in assets/hsp-description-blocks.css; the markup is rendered
   server-side by snippets/hsp-render-description.liquid. Everything here is
   optional polish: if this file never loads, tables still render fully styled
   and horizontally scrollable. Nothing below is required to read the data.

   Two jobs:
     1. Mobile card-stack (layout="stack") — copy each column's <th> text onto
        the matching <td> as data-hsp-label, then flag the figure with
        data-hsp-stack-ready. The CSS gates the stacked layout on that flag, so
        without JS the table falls back to scrolling rather than collapsing into
        an unlabeled pile of cells.
     2. Scroll affordances — .is-scrollable when the table actually overflows,
        .is-scrolled once the user has scrolled off the left edge (reveals the
        sticky first column's separator shadow), .is-scrolled-end at the right
        edge (drops the "more to see" fade).
   ============================================================ */
(function () {
  'use strict';

  var SELECTOR = '.hsp-table';

  /* Copy header text down each column. Honours colspan so a spanning header
     labels every column it covers; skips rows with no thead to read from. */
  function applyLabels(figure) {
    if (figure.hasAttribute('data-hsp-stack-ready')) return;

    /* The author writes the <table> by hand, so it is matched by tag, never by
       a class we injected — see the note in hsp-description-blocks.css. */
    var table = figure.querySelector('table');
    if (!table) return;

    var headRow = table.querySelector('thead tr');
    if (!headRow) return;

    var labels = [];
    Array.prototype.forEach.call(headRow.cells, function (cell) {
      var text = (cell.textContent || '').trim();
      var span = cell.colSpan || 1;
      for (var i = 0; i < span; i++) labels.push(text);
    });
    if (!labels.length) return;

    Array.prototype.forEach.call(table.tBodies, function (body) {
      Array.prototype.forEach.call(body.rows, function (row) {
        var col = 0;
        Array.prototype.forEach.call(row.cells, function (cell) {
          var label = labels[col];
          if (label && !cell.hasAttribute('data-hsp-label')) {
            cell.setAttribute('data-hsp-label', label);
          }
          col += cell.colSpan || 1;
        });
      });
    });

    figure.setAttribute('data-hsp-stack-ready', '');
  }

  /* Tolerance absorbs sub-pixel rounding in the scrollWidth/clientWidth
     comparison, which otherwise leaves a permanent 1px "scrollable" state. */
  function syncScrollState(figure) {
    var max = figure.scrollWidth - figure.clientWidth;
    var scrollable = max > 1;

    figure.classList.toggle('is-scrollable', scrollable);
    figure.classList.toggle('is-scrolled', scrollable && figure.scrollLeft > 1);
    figure.classList.toggle('is-scrolled-end', scrollable && figure.scrollLeft >= max - 1);

    /* Only advertise the region to assistive tech (and put it in the tab order)
       when there is something hidden to scroll to. A non-overflowing table is
       just a table. */
    if (scrollable) {
      figure.setAttribute('tabindex', '0');
    } else {
      figure.removeAttribute('tabindex');
    }
  }

  function init(figure) {
    if (figure.hsPTableReady) return;
    figure.hsPTableReady = true;

    if (figure.getAttribute('data-hsp-layout') === 'stack') applyLabels(figure);

    figure.addEventListener('scroll', function () {
      syncScrollState(figure);
    }, { passive: true });

    syncScrollState(figure);

    /* Column widths shift on resize, on font load, and when the stacked layout
       engages or disengages at the 640px breakpoint. */
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        syncScrollState(figure);
      }).observe(figure);
    }
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme-editor section reloads and the collection grid's filter re-render both
     replace markup without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
